import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, ArrowLeft, Phone, Mail, MapPin, Filter, MoreVertical } from 'lucide-react';

const ViewMyAppointment = ({ onBack }) => {
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'today', 'upcoming', 'past'
  const [loading, setLoading] = useState(true);

  // Mock data for appointments
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const mockAppointments = [
        {
          id: 1,
          patientName: 'John Doe',
          patientId: 'P001',
          date: '2025-09-15',
          time: '10:00 AM',
          duration: '30 mins',
          type: 'Scheduled',
          status: 'scheduled', // scheduled, completed, cancelled
          contact: '+91 9876543210',
          email: 'john.doe@example.com',
          address: '123 Main St, City, State'
        },
        {
          id: 2,
          patientName: 'Sarah Johnson',
          patientId: 'P002',
          date: '2025-09-15',
          time: '11:30 AM',
          duration: '45 mins',
          type: 'Scheduled',
          status: 'scheduled',
          contact: '+91 9876543211',
          email: 'sarah.j@example.com',
          address: '456 Oak Ave, City, State'
        },
        {
          id: 3,
          patientName: 'Robert Brown',
          patientId: 'P003',
          date: '2025-09-14',
          time: '02:15 PM',
          duration: '60 mins',
          type: 'Scheduled',
          status: 'completed',
          contact: '+91 9876543212',
          email: 'robert.b@example.com',
          address: '789 Pine Rd, City, State'
        },
        {
          id: 4,
          patientName: 'Emily Davis',
          patientId: 'P004',
          date: '2025-09-16',
          time: '09:00 AM',
          duration: '30 mins',
          type: 'Scheduled',
          status: 'scheduled',
          contact: '+91 9876543213',
          email: 'emily.d@example.com',
          address: '321 Elm St, City, State'
        },
        {
          id: 5,
          patientName: 'Michael Wilson',
          patientId: 'P005',
          date: '2025-09-13',
          time: '03:45 PM',
          duration: '45 mins',
          type: 'Scheduled',
          status: 'cancelled',
          contact: '+91 9876543214',
          email: 'michael.w@example.com',
          address: '654 Maple Dr, City, State'
        }
      ];
      setAppointments(mockAppointments);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border border-red-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const filterAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    
    switch (filter) {
      case 'today':
        return appointments.filter(app => app.date === today);
      case 'upcoming':
        return appointments.filter(app => app.date >= today && app.status === 'scheduled');
      case 'past':
        return appointments.filter(app => app.date < today || app.status === 'completed' || app.status === 'cancelled');
      default:
        return appointments;
    }
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const filteredAppointments = filterAppointments();

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-sm p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
        <div className="flex items-center mb-4 sm:mb-0">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800 mr-4 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-800">My Appointments</h1>
        </div>
        <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-transparent border-0 text-sm focus:outline-none focus:ring-0"
          >
            <option value="all">All Appointments</option>
            <option value="today">Today</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredAppointments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No appointments found</h3>
          <p className="text-gray-500">You don't have any appointments for the selected filter.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAppointments.map((appointment) => (
            <div key={appointment.id} className="border border-gray-100 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex flex-col lg:flex-row">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                    <div className="flex items-center mb-2 sm:mb-0">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {appointment.patientName}
                        </h3>
                        <p className="text-sm text-gray-500">ID: {appointment.patientId}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                      {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{formatDate(appointment.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{appointment.time} ({appointment.duration})</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{appointment.contact}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="truncate">{appointment.email}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start text-gray-600">
                    <MapPin className="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm">{appointment.address}</span>
                  </div>
                </div>
                
                <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col sm:flex-row lg:flex-col gap-2 justify-center">
                  {appointment.status === 'scheduled' && (
                    <>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm">
                        Reschedule
                      </button>
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors shadow-sm">
                        Completed
                      </button>
                      <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm">
                        Cancel
                      </button>
                    </>
                  )}
                  {(appointment.status === 'completed' || appointment.status === 'cancelled') && (
                    <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors">
                      View Details
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ViewMyAppointment;